import React, { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import schemify from 'url-schemify';
import { useTranslation } from 'react-i18next';

import { showModal } from 'actions/modal';

import { validateUrl } from 'utils/validators';

import EmbedInput from 'components/EmbedInput';
import PromptPopout from 'components/prompt-popout';

import IconRemove from 'icons/icon-remove';
import IconEdit from 'icons/icon-edit';
import IconExpand from 'icons/icon-expand';

import './EmbedPane.styles.css';

const EmbedPane = ({ url, isImage, name, fullScreen, expandDisabled, onChange, onRemove }) => {
    const [editing, setEditing] = useState(!isImage && !url);
    const { t } = useTranslation(['embed', 'common']);
    const fieldRef = useRef(null);

    const schemifiedUrl = useMemo(() => {
        return validateUrl(url) && schemify(url).replace('$HOSTNAME$', window.location.hostname);;
    }, [url]);

    const handleEditClick = () => {
        setEditing(true);
        setTimeout(() => {
            fieldRef.current?.focus()
        }, 200);
    };

    const handleExpand = () => {
        showModal('EMBED_PREVIEW_MODAL', {
            url,
            isImage,
            onChange,
            onRemove
        });
    }

    const handleChange = useCallback(nextValue => {
        onChange(nextValue);

        setEditing(!nextValue);
    }, [onChange]);

    return (
        <div className={'embed-pane' + (fullScreen ? ' fullscreen' : '') + (editing ? ' editing' : '')}>
            <div className="embed-pane-head">
                <div className="embed-pane-controls">
                    <div className="embed-pane-controls-inner">
                        {
                            !fullScreen ? (
                                <React.Fragment>
                                    {
                                        !expandDisabled ? (
                                            <button className="btn btn-icon" onClick={handleExpand}><IconExpand size="15px" /></button>
                                        ) : null
                                    }

                                    {
                                        onChange ? (
                                            <button className="btn btn-icon" onClick={handleEditClick}><IconEdit size="14px" /></button>
                                        ) : null
                                    }

                                </React.Fragment>
                            ) : null
                        }

                        {
                            onRemove ? (
                                <PromptPopout
                                    title={t(`actions.${isImage ? 'removeImage' : 'remove'}.title`)}
                                    text={t(`actions.${isImage ? 'removeImage' : 'remove'}.description`)}
                                    confirmText={ t('common:Remove') }
                                    confirmClassName="btn-caution"
                                >
                                    <button className="btn btn-icon action-danger" onClick={onRemove}><IconRemove /></button>
                                </PromptPopout>
                            ) : null
                        }
                    </div>
                </div>

                {
                    onChange ? (
                        <div className="embed-pane-edit-field" contentEditable={false} style={{ userSelect: 'none' }}>
                            <EmbedInput small={true} changeOnBlur={true} value={url} autoFocus={!url} onChange={handleChange} provideController={fieldRef} />

                            {
                                !fullScreen ? (
                                    <React.Fragment>
                                        <button className="btn btn-success btn-small">{ t('common:Save') }</button>

                                        {
                                            onRemove ? (
                                                <PromptPopout
                                                    title={t(`actions.${isImage ? 'removeImage' : 'remove'}.title`)}
                                                    text={t(`actions.${isImage ? 'removeImage' : 'remove'}.description`)}
                                                    confirmText={ t('common:Remove') }
                                                    confirmClassName="btn-caution"
                                                >
                                                    <button className="btn btn-icon action-danger" onClick={onRemove}><IconRemove /></button>
                                                </PromptPopout>
                                            ) : null
                                        }
                                    </React.Fragment>
                                ) : null
                            }
                        </div>
                    ) : null
                }
            </div>

            <div className={'embed-pane-preview' + (isImage ? ' type-image' : '') + (schemifiedUrl ? ' populated' : '')}>
                {
                    schemifiedUrl ?
                        isImage ? (
                            <img src={schemifiedUrl} alt={name} />
                        ) : (
                            <iframe src={schemifiedUrl} allow="midi; geolocation; microphone; camera; display-capture; encrypted-media;" sandbox="allow-modals allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation allow-downloads" />
                        )
                    : <p className="info">{ t('noValidUrl')}</p>
                }
            </div>
        </div>
    )
}

export default EmbedPane;
